import { useCallback, useEffect, useState } from "react";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import { setSelectedBillId } from "../../../../../store";
import { useAppDispatch } from "../../../../../store/hooks";

interface Options {
  /** Избраната маса в момента. */
  selectedBillId: number | null;
  /** Избраната е в списъка (`GET /bills`). */
  hasSelectedBill: boolean;
  /** `useCloseBillTarget().requestFiscalInvoiceClose` — „Закрий" с купувача. */
  requestFiscalInvoiceClose: (fiscalInvoiceBuyer: CloseBillFiscalInvoiceIn) => Promise<void>;
}

/**
 * „Фактуриране" → отворена сметка → купувач → СЪЩИЯТ „Закрий" за нея —
 * пътят на „Смяна на начин на плащане" (`TableFooter`): сметката става
 * избраната, изчаква се изборът да стигне до лентата и „Закрий" се отваря
 * през `useCloseBillTarget` („Брой гости", проверката „още ли е отворена"),
 * само че с купувача в целта. Избере ли операторът друга маса междувременно —
 * отказ, вместо „Закрий" изневиделица.
 */
export function useFiscalInvoiceClose({
  selectedBillId,
  hasSelectedBill,
  requestFiscalInvoiceClose,
}: Options) {
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState<{
    billId: number;
    fiscalInvoice: CloseBillFiscalInvoiceIn;
  } | null>(null);

  // Изборът и чакането — в ЕДИН handler, за да стигнат до следващия рендер
  // заедно (иначе старият избор би отменил заявката веднага).
  const request = useCallback(
    (bill: BillOut, fiscalInvoice: CloseBillFiscalInvoiceIn) => {
      dispatch(setSelectedBillId(bill.id));
      setPending({ billId: bill.id, fiscalInvoice });
    },
    [dispatch],
  );

  useEffect(() => {
    if (pending == null) return;
    if (selectedBillId != null && selectedBillId !== pending.billId) {
      setPending(null);
      return;
    }
    if (selectedBillId !== pending.billId || !hasSelectedBill) return;
    void requestFiscalInvoiceClose(pending.fiscalInvoice);
    setPending(null);
  }, [pending, selectedBillId, hasSelectedBill, requestFiscalInvoiceClose]);

  return request;
}
